"use client";

// ── Upload a solution: pick the language, read the file, then put it in ──────
// `EX-EDR03`, Wave 23 decisions 2 and 3 (canvas boards Pick, Preview, Mismatch,
// Checks, PhoneUpload). The file is read in the browser and checked before it
// can reach the editor (`features/judge/lib/source-file.ts`); nothing leaves the
// page until a submit, and then only the text, through the ordinary submit.
//
// "Put in editor" is the primary action (D1): the reader sees the code land
// where they can still read it, run it and Undo it. "Submit this file" puts it
// in and submits in one press, for the reader who already ran it locally.

import { useCallback, useEffect, useId, useMemo, useRef, useState, type DragEvent, type ReactNode } from "react";
import { AlertTriangle, Check, FileUp, Info, Loader2, Send, Wand2, XCircle } from "lucide-react";
import { useTranslations } from "next-intl";

import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/ui/status-badge";
import { PlaygroundEditor } from "@/features/playground/components/PlaygroundEditor";
import { useEditorSettingsState } from "@/features/playground/lib/use-editor-settings";
import { CODING_SOURCE_CODE_MAX } from "@/features/quiz/coding-task-api";
import { formatRuntimeLanguageName, getRuntimeMonacoLanguage } from "@/features/quiz/lib/coding-task-format";
import {
  SOURCE_FILE_LANGUAGE_BY_EXTENSION,
  fileExtensionOf,
  javaPublicClass,
  languageForFileName,
  readSourceFile,
  renameJavaClassToMain,
  runtimeForLanguage,
  sourceExtensionsFor,
  type SourceFileRefusal,
  type SourceFileText,
} from "../../lib/source-file";
import { RuntimePicker, runtimeLabel } from "./EditorToolbarControls";
import type { AllowedRuntime } from "./use-solution-draft";

export interface UploadedSource {
  /** What lands in the editor: normalized, and renamed to `Main` if the reader asked. */
  text: string;
  runtime: AllowedRuntime;
  fileName: string;
}

type Stage =
  | { kind: "pick" }
  | { kind: "reading"; name: string }
  | { kind: "several"; count: number }
  | { kind: "refused"; name: string; refusal: SourceFileRefusal }
  | { kind: "preview"; name: string; file: SourceFileText };

/** Every extension the picker suggests; a drop is not filtered and is checked instead. */
const ACCEPT = [...SOURCE_FILE_LANGUAGE_BY_EXTENSION.keys(), ".txt"].join(",");

const ALERT = "flex flex-wrap items-start gap-x-2.5 gap-y-2 rounded-lg border px-3 py-2.5 text-[13px] leading-relaxed";
const ALERT_WARN = "border-amber-500/30 bg-amber-500/10 text-amber-100";
const ALERT_STOP = "border-red-500/30 bg-red-500/10 text-red-100";

function kilobytes(bytes: number): number {
  return Math.max(0.1, Math.round(bytes / 102.4) / 10);
}

function megabytes(bytes: number): number {
  return Math.round(bytes / (1024 * 102.4)) / 10;
}

export function UploadSourceDialog({
  onClose,
  allowedRuntimes,
  current,
  problemTitle,
  problemLabel,
  editorLines,
  initialFiles,
  canSubmit,
  submitting,
  onPut,
}: {
  onClose: () => void;
  allowedRuntimes: AllowedRuntime[];
  /** The editor's runtime, the dialog's starting choice. */
  current: AllowedRuntime;
  /** Under the title: "B · Pairs Divisible by K", or the practice problem's title. */
  problemTitle: string;
  /** In "Your editor has 10 lines for B": the letter, or the title. */
  problemLabel: string;
  editorLines: number;
  /** A drop on the editor: open on these, at the preview or the reason it cannot be read. */
  initialFiles: readonly File[] | null;
  /** False where Submit is replaced by a refusal (ended, not registered, staff, signed out). */
  canSubmit: boolean;
  submitting: boolean;
  onPut: (source: UploadedSource, submit: boolean) => void;
}) {
  const t = useTranslations("judge.upload");
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [editorSettings] = useEditorSettingsState();

  const [runtime, setRuntime] = useState<AllowedRuntime>(current);
  const [stage, setStage] = useState<Stage>({ kind: "pick" });
  /** "Keep Python": the reader chose their language over the file's name. */
  const [keepLanguage, setKeepLanguage] = useState(false);
  const [javaChoice, setJavaChoice] = useState<"ask" | "renamed" | "kept">("ask");
  const [dragging, setDragging] = useState(false);
  /** Only the newest read may land: a slow file chosen first must not replace a quick second one. */
  const readToken = useRef(0);

  const languages = useMemo(() => {
    const seen: string[] = [];
    for (const entry of allowedRuntimes) {
      if (!seen.includes(entry.languageKey)) seen.push(entry.languageKey);
    }
    return seen;
  }, [allowedRuntimes]);
  const versionsOfLanguage = useMemo(
    () => allowedRuntimes.filter((entry) => entry.languageKey === runtime.languageKey),
    [allowedRuntimes, runtime.languageKey],
  );

  const accept = useCallback(async (files: readonly File[]) => {
    const token = (readToken.current += 1);
    if (files.length === 0) return;
    if (files.length > 1) {
      setStage({ kind: "several", count: files.length });
      return;
    }
    const chosen = files[0]!;
    setStage({ kind: "reading", name: chosen.name });
    const result = await readSourceFile(chosen);
    if (token !== readToken.current) return;
    setKeepLanguage(false);
    setJavaChoice("ask");
    setStage(
      result.ok
        ? { kind: "preview", name: chosen.name, file: result.file }
        : { kind: "refused", name: chosen.name, refusal: result.refusal },
    );
  }, []);

  useEffect(() => {
    if (initialFiles && initialFiles.length > 0) void accept(initialFiles);
    // Once, for the drop that opened the dialog.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const chooseLanguage = (languageKey: string) => {
    const next = runtimeForLanguage(allowedRuntimes, languageKey, current);
    if (next) setRuntime(next);
    setKeepLanguage(false);
  };

  const chooseAnother = () => {
    readToken.current += 1;
    setStage({ kind: "pick" });
    // The picker opens from a press, so a keyboard reader lands back on it.
    requestAnimationFrame(() => inputRef.current?.click());
  };

  const onDragOver = (event: DragEvent) => {
    if (!event.dataTransfer.types.includes("Files")) return;
    event.preventDefault();
    setDragging(true);
  };
  const onDrop = (event: DragEvent) => {
    if (!event.dataTransfer.types.includes("Files")) return;
    event.preventDefault();
    setDragging(false);
    void accept(Array.from(event.dataTransfer.files));
  };

  const languageName = formatRuntimeLanguageName(runtime.languageKey);
  const extensions = sourceExtensionsFor(runtime.languageKey);

  /* ── The preview's derived state ─────────────────────────────────────────── */
  const preview = stage.kind === "preview" ? stage : null;
  const fileExtension = preview ? fileExtensionOf(preview.name) : null;
  const fileLanguage = preview ? languageForFileName(preview.name) : null;
  const fileRuntime = fileLanguage ? runtimeForLanguage(allowedRuntimes, fileLanguage, current) : null;
  const mismatch = fileLanguage !== null && fileLanguage !== runtime.languageKey && !keepLanguage;
  const javaClass = preview && runtime.languageKey === "java" ? javaPublicClass(preview.file.text) : null;
  const askJava = javaClass !== null && javaClass.name !== "Main" && javaChoice === "ask";
  const text = preview
    ? javaChoice === "renamed" && runtime.languageKey === "java"
      ? renameJavaClassToMain(preview.file.text)
      : preview.file.text
    : "";
  const blocked = mismatch || submitting;

  const put = (submit: boolean) => {
    if (!preview || blocked) return;
    onPut({ text, runtime, fileName: preview.name }, submit);
  };

  const refusalMessage = (refused: Extract<Stage, { kind: "refused" }>) => {
    const bold = (chunks: ReactNode) => <b className="font-semibold">{chunks}</b>;
    const { name, refusal } = refused;
    switch (refusal.reason) {
      case "empty":
        return t.rich("refusedEmpty", { b: bold, name });
      case "binary":
        return t.rich("refusedBinary", { b: bold, name, example: extensions[0] ?? ".cpp" });
      case "tooLong":
        return refusal.chars === null
          ? t.rich("refusedTooBig", { b: bold, name, mb: megabytes(refusal.bytes), max: CODING_SOURCE_CODE_MAX })
          : t.rich("refusedTooLong", { b: bold, name, chars: refusal.chars, max: CODING_SOURCE_CODE_MAX });
      default:
        return t.rich("refusedUnreadable", { b: bold, name });
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t("title")}
      description={problemTitle}
      closeLabel={t("close")}
      tone="dark"
      dismissible={!submitting}
      className="max-w-2xl"
    >
      <div className="space-y-4" data-testid="upload-dialog">
        {/* 1. The language, as pills, with the version beside them */}
        <section aria-labelledby={`${inputId}-language`} className="space-y-2">
          <div className="flex items-center gap-2">
            <span id={`${inputId}-language`} className="text-sm font-semibold text-white">
              {t("stepLanguage")}
            </span>
            <span className="ml-auto flex items-center gap-2 text-xs text-white/55">
              {t("version")}
              <RuntimePicker
                allowedRuntimes={versionsOfLanguage}
                languageKey={runtime.languageKey}
                languageVersion={runtime.languageVersion}
                onChange={setRuntime}
              />
            </span>
          </div>
          <div className="flex flex-wrap gap-2" role="group" aria-label={t("stepLanguage")}>
            {languages.map((key) => (
              <Button
                key={key}
                type="button"
                variant="outlineDark"
                size="sm"
                aria-pressed={key === runtime.languageKey}
                onClick={() => chooseLanguage(key)}
              >
                {formatRuntimeLanguageName(key)}
              </Button>
            ))}
          </div>
        </section>

        {preview ? (
          <>
            {/* 2. The file, whole, before it goes anywhere */}
            <div className="flex items-center gap-3">
              <span
                aria-hidden
                className="flex h-10 w-8 shrink-0 items-end justify-center rounded-md bg-white/10 pb-1 text-[9px] font-bold uppercase text-accent-peach"
              >
                {(fileExtension ?? "").replace(".", "") || "txt"}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-white" data-testid="upload-file-name">
                  {preview.name}
                </p>
                <p className="text-xs text-white/55">
                  {t("stats", { kb: kilobytes(preview.file.bytes), lines: preview.file.lines, chars: preview.file.chars })}
                </p>
              </div>
              <Button type="button" variant="ghostDark" size="sm" onClick={chooseAnother}>
                {t("chooseAnother")}
              </Button>
            </div>

            {mismatch && fileLanguage ? (
              <div role="alert" className={`${ALERT} ${ALERT_WARN}`} data-testid="upload-mismatch">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" aria-hidden />
                <p className="min-w-0 flex-1">
                  {fileRuntime
                    ? t.rich("mismatch", {
                        b: (chunks) => <b className="font-semibold">{chunks}</b>,
                        name: preview.name,
                        fileLanguage: formatRuntimeLanguageName(fileLanguage),
                        selected: languageName,
                      })
                    : t.rich("mismatchNotOffered", {
                        b: (chunks) => <b className="font-semibold">{chunks}</b>,
                        name: preview.name,
                        fileLanguage: formatRuntimeLanguageName(fileLanguage),
                        selected: languageName,
                      })}
                </p>
                <span className="flex basis-full flex-wrap gap-2 pl-6">
                  {fileRuntime ? (
                    <Button type="button" size="sm" onClick={() => setRuntime(fileRuntime)}>
                      {t("useRuntime", { runtime: runtimeLabel(fileRuntime.languageKey, fileRuntime.languageVersion) })}
                    </Button>
                  ) : null}
                  <Button type="button" variant="ghostDark" size="sm" onClick={() => setKeepLanguage(true)}>
                    {t("keepLanguage", { language: languageName })}
                  </Button>
                </span>
              </div>
            ) : null}

            {askJava && javaClass ? (
              <div role="status" className={`${ALERT} ${ALERT_WARN}`} data-testid="upload-java">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" aria-hidden />
                <p className="min-w-0 flex-1">
                  {t.rich("javaClass", {
                    b: (chunks) => <b className="font-semibold">{chunks}</b>,
                    code: (chunks) => <code className="font-mono text-[12px]">{chunks}</code>,
                    name: javaClass.name,
                  })}
                </p>
                <span className="flex basis-full flex-wrap gap-2 pl-6">
                  <Button type="button" size="sm" onClick={() => setJavaChoice("renamed")}>
                    <Wand2 className="h-3.5 w-3.5" aria-hidden />
                    {t("javaRename")}
                  </Button>
                  <Button type="button" variant="ghostDark" size="sm" onClick={() => setJavaChoice("kept")}>
                    {t("javaKeep")}
                  </Button>
                </span>
              </div>
            ) : null}

            {/* A flex column, so the editor's `fillParent` root takes the whole height. */}
            <div className="flex h-[min(40vh,340px)] flex-col overflow-hidden rounded-lg" data-testid="upload-preview">
              <PlaygroundEditor
                fillParent
                fillHeight
                readOnly
                language={getRuntimeMonacoLanguage(runtime.languageKey)}
                value={text}
                onChange={() => undefined}
                settings={editorSettings}
                onRun={() => undefined}
                ariaLabel={t("previewLabel", { name: preview.name })}
                darkTheme="progsity-judge"
              />
            </div>

            <ul className="flex flex-wrap gap-1.5" aria-label={t("checksLabel")}>
              <li>
                <StatusBadge onDark tone="success" icon={Check} label={t("checkText")} />
              </li>
              <li>
                <StatusBadge
                  onDark
                  tone="success"
                  icon={Check}
                  label={t("checkLength", { chars: preview.file.chars, max: CODING_SOURCE_CODE_MAX })}
                />
              </li>
              <li>
                {fileLanguage === null ? (
                  <StatusBadge
                    onDark
                    tone="neutral"
                    icon={Info}
                    label={fileExtension ? t("checkLanguageUnknown", { ext: fileExtension }) : t("checkLanguageNoExtension")}
                  />
                ) : fileLanguage === runtime.languageKey ? (
                  <StatusBadge
                    onDark
                    tone="success"
                    icon={Check}
                    label={t("checkLanguageMatches", { language: languageName, ext: fileExtension ?? "" })}
                  />
                ) : (
                  <StatusBadge
                    onDark
                    tone="warning"
                    icon={AlertTriangle}
                    label={t("checkLanguageOff", { language: languageName, ext: fileExtension ?? "" })}
                  />
                )}
              </li>
              {preview.file.lineEndingsFixed ? (
                <li>
                  <StatusBadge onDark tone="success" icon={Check} label={t("checkLineEndings")} />
                </li>
              ) : null}
              {javaChoice === "renamed" && javaClass ? (
                <li>
                  <StatusBadge onDark tone="success" icon={Check} label={t("checkRenamed")} />
                </li>
              ) : null}
            </ul>

            <p className="text-xs text-white/60" data-testid="upload-replaces">
              {t("replaces", { lines: editorLines, problem: problemLabel })}
            </p>

            <div className="flex flex-wrap items-center gap-2 border-t border-white/10 pt-4">
              <Button type="button" variant="ghostDark" size="sm" onClick={onClose} disabled={submitting}>
                {t("cancel")}
              </Button>
              <span className="flex-1" />
              {canSubmit ? (
                <Button type="button" variant="outlineDark" size="pill" disabled={blocked} onClick={() => put(true)}>
                  {submitting ? (
                    <Loader2 className="h-4 w-4 motion-safe:animate-spin" aria-hidden />
                  ) : (
                    <Send className="h-4 w-4" aria-hidden />
                  )}
                  {t("submitFile")}
                </Button>
              ) : null}
              <Button type="button" size="pill" disabled={blocked} onClick={() => put(false)} data-autofocus>
                <FileUp className="h-4 w-4" aria-hidden />
                {t("putIn")}
              </Button>
            </div>
          </>
        ) : (
          <>
            {/* 2. The file: a drop zone, which is also the picker */}
            <section className="space-y-2">
              <span className="text-sm font-semibold text-white">{t("stepFile")}</span>

              {stage.kind === "several" || stage.kind === "refused" ? (
                <div role="alert" className={`${ALERT} ${ALERT_STOP}`} data-testid="upload-refused">
                  <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-300" aria-hidden />
                  <p className="min-w-0 flex-1">
                    {stage.kind === "several"
                      ? t.rich("refusedSeveral", {
                          b: (chunks) => <b className="font-semibold">{chunks}</b>,
                          count: stage.count,
                          problem: problemLabel,
                        })
                      : refusalMessage(stage)}
                  </p>
                </div>
              ) : null}

              <label
                htmlFor={inputId}
                onDragOver={onDragOver}
                onDragLeave={() => setDragging(false)}
                onDrop={onDrop}
                className={`flex min-h-40 cursor-pointer flex-col items-center justify-center gap-1.5 rounded-xl border-[1.5px] border-dashed px-4 py-6 text-center transition-colors focus-within:ring-2 focus-within:ring-primary ${
                  dragging ? "border-primary bg-primary/10" : "border-white/20 bg-white/[0.03] hover:bg-white/[0.06]"
                }`}
                data-testid="upload-drop-zone"
              >
                {stage.kind === "reading" ? (
                  <span className="flex items-center gap-2 text-sm text-white/70">
                    <Loader2 className="h-4 w-4 motion-safe:animate-spin" aria-hidden />
                    {t("reading", { name: stage.name })}
                  </span>
                ) : (
                  <>
                    <FileUp className="h-6 w-6 text-white/50" aria-hidden />
                    <span className="text-[15px] font-semibold text-white">{t("dropHere", { language: languageName })}</span>
                    <span className="text-[13px] text-white/60">
                      {t.rich("orChoose", {
                        choose: (chunks) => <span className="font-semibold text-accent-peach">{chunks}</span>,
                        extensions: extensions.join(", "),
                      })}
                    </span>
                  </>
                )}
                <input
                  ref={inputRef}
                  id={inputId}
                  type="file"
                  accept={ACCEPT}
                  className="sr-only"
                  data-autofocus
                  data-testid="upload-input"
                  onChange={(event) => {
                    const files = Array.from(event.target.files ?? []);
                    // The same file chosen twice must still fire a change.
                    event.target.value = "";
                    void accept(files);
                  }}
                />
              </label>
              <p className="text-xs text-white/50">{t("note", { max: CODING_SOURCE_CODE_MAX })}</p>
            </section>

            <div className="flex justify-end border-t border-white/10 pt-4">
              <Button type="button" variant="ghostDark" size="sm" onClick={onClose}>
                {t("cancel")}
              </Button>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
}
